import { useEffect, useState } from 'react'
import { api } from '@/lib/api'
import type { PolicyArtefactType } from '@/lib/apiTypes'
import { Tooltip } from '@/shared/components/Tooltip'

/** Minimal JSON-Schema shape we render. */
interface Node {
  type?: string | string[]
  description?: string
  properties?: Record<string, Node>
  required?: string[]
  items?: Node
  enum?: unknown[]
  const?: unknown
  $ref?: string
  title?: string
}

interface SchemaDoc extends Node {
  $defs?: Record<string, Node>
}

const refName = (ref: string) => ref.split('/').pop() ?? ref

/** Human type label for a property node, e.g. `string`, `Summary`, `FindingPrimitive[]`,
 *  `"module"`, `"critical" | "high" | …`, `object`. `link` returns the sub-table anchor a
 *  reader should jump to (or null). */
function typeInfo(node: Node, defs: Record<string, Node>, path: string): { label: string; nullable: boolean } {
  if (node.const !== undefined) return { label: `"${String(node.const)}"`, nullable: false }
  if (node.enum) {
    const vals = node.enum.filter((e) => e !== null)
    return { label: vals.map((e) => `"${String(e)}"`).join(' | '), nullable: node.enum.includes(null) }
  }
  if (node.$ref) return { label: refName(node.$ref), nullable: false }
  const types = Array.isArray(node.type) ? node.type : node.type ? [node.type] : []
  const nullable = types.includes('null')
  const base = types.filter((t) => t !== 'null')[0]
  if (base === 'array' && node.items) {
    if (node.items.$ref) return { label: `${refName(node.items.$ref)}[]`, nullable }
    // Array of inline objects → link to the `${path}[]` sub-table (don't double-suffix).
    if (node.items.properties) return { label: `${path}[]`, nullable }
    const it = typeInfo(node.items, defs, `${path}[]`)
    return { label: `${it.label}[]`, nullable }
  }
  if (base === 'object' && node.properties) return { label: path, nullable } // link to sub-table
  return { label: base ?? 'any', nullable }
}

/** One type rendered as a Field / Type / Description table. */
function TypeTable({ title, node, defs }: { title: string; node: Node; defs: Record<string, Node> }) {
  const props = node.properties ?? {}
  const required = new Set(node.required ?? [])
  return (
    <div id={`t-${title}`} style={{ marginBottom: 22 }}>
      <div style={{ fontFamily: 'var(--mono)', fontSize: '0.82rem', color: 'var(--accent)', marginBottom: 4 }}>{title}</div>
      {node.description && <div style={{ fontSize: '0.76rem', color: 'var(--text2)', marginBottom: 8 }}>{node.description}</div>}
      <div className="table-wrap fu">
        <table>
          <thead>
            <tr><th style={{ width: '26%' }}><Tooltip text="Field name. * marks required, ? marks nullable." placement="top"><span tabIndex={0} className="cursor-help">Field</span></Tooltip></th><th style={{ width: '28%' }}><Tooltip text="The field's JSON type; object and array types link to their own table." placement="top"><span tabIndex={0} className="cursor-help">Type</span></Tooltip></th><th><Tooltip text="The field's description from the schema." placement="top"><span tabIndex={0} className="cursor-help">Description</span></Tooltip></th></tr>
          </thead>
          <tbody>
            {Object.entries(props).map(([key, child]) => {
              const info = typeInfo(child, defs, title === 'input' ? key : `${title}.${key}`)
              return (
                <tr key={key}>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: '0.76rem', color: 'var(--text)', whiteSpace: 'nowrap' }}>
                    {key}
                    {required.has(key) && <span style={{ color: 'var(--red)', marginLeft: 4 }} title="required">*</span>}
                    {info.nullable && <span className="dim" title="nullable">?</span>}
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: '0.72rem', color: 'var(--text2)' }}>{info.label}</td>
                  <td style={{ fontSize: '0.74rem', color: 'var(--text2)' }}>{child.description ?? ''}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** Breadth-first collection of object types to render, from a root node. Inline nested
 *  objects get a dotted-path title; $ref targets get the ref name (deduped). */
function collect(root: Node, defs: Record<string, Node>): Array<{ title: string; node: Node }> {
  const out: Array<{ title: string; node: Node }> = []
  const seen = new Set<string>()
  const queue: Array<{ title: string; node: Node }> = [{ title: 'input', node: root }]
  while (queue.length) {
    const { title, node } = queue.shift()!
    const resolved = node.$ref ? defs[refName(node.$ref)] : node
    if (!resolved?.properties || seen.has(title)) continue
    seen.add(title)
    out.push({ title, node: resolved })
    for (const [key, child] of Object.entries(resolved.properties)) {
      const path = title === 'input' ? key : `${title}.${key}`
      if (child.$ref) queue.push({ title: refName(child.$ref), node: child })
      else if (child.items?.$ref) queue.push({ title: refName(child.items.$ref), node: child.items })
      else if ((Array.isArray(child.type) ? child.type.includes('object') : child.type === 'object') && child.properties) {
        queue.push({ title: path, node: child })
      } else if (child.type === 'array' && child.items?.properties) {
        queue.push({ title: `${path}[]`, node: child.items })
      }
    }
  }
  return out
}

/**
 * API-reference view of the OPA policy `input` for one artefact, rendered from the live
 * JSON Schema at /api/policy-schema/{artefact} — field/type/description tables per object
 * type (like a generated API reference), so it can never drift from the contract.
 */
export function SchemaReference({ artefact }: { artefact: PolicyArtefactType }) {
  const [doc, setDoc]     = useState<SchemaDoc | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    api.get<SchemaDoc>(`/api/policy-schema/${artefact}`)
      .then((d) => { if (live) setDoc(d) })
      .catch((e) => { if (live) setError((e as Error).message) })
    return () => { live = false }
  }, [artefact])

  if (error) return <div className="feedback-error">Couldn’t load schema: {error}</div>
  if (!doc) return <span className="spinner" />

  const defs = doc.$defs ?? {}
  const tables = collect(doc, defs)
  // Also render referenced $defs enums (e.g. Severity) that carry an enum but no properties.
  const enumDefs = Object.entries(defs).filter(([, n]) => n.enum && !n.properties)

  return (
    <div>
      <div style={{ fontSize: '0.72rem', color: 'var(--text3)', marginBottom: 12 }}>
        <span style={{ color: 'var(--red)' }}>*</span> required · <span>?</span> nullable · types link to their table below.
      </div>
      {tables.map((t) => <TypeTable key={t.title} title={t.title} node={t.node} defs={defs} />)}
      {enumDefs.map(([name, n]) => (
        <div key={name} style={{ marginBottom: 16 }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: '0.82rem', color: 'var(--accent)', marginBottom: 4 }}>{name}</div>
          {n.description && <div style={{ fontSize: '0.76rem', color: 'var(--text2)', marginBottom: 4 }}>{n.description}</div>}
          <div style={{ fontFamily: 'var(--mono)', fontSize: '0.74rem', color: 'var(--text2)' }}>
            {(n.enum ?? []).map((e) => `"${String(e)}"`).join(' | ')}
          </div>
        </div>
      ))}
    </div>
  )
}
